import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { Plant } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';

interface PlantListProps {
  plants: Plant[];
  loading: boolean;
  /** 刚添加的植物 id,高亮显示 */
  highlightId?: string | null;
  emptyHint?: string;
  showEmptyAddLink?: boolean;
}

export function PlantList({ plants, loading, highlightId, emptyHint, showEmptyAddLink }: PlantListProps) {
  if (loading) {
    return <p className="mt-2 text-xs text-slate-500">加载中…</p>;
  }
  if (plants.length === 0) {
    return (
      <div className="mt-3 text-center">
        <p className="text-xs text-slate-500">{emptyHint ?? '还没有植物 🌱'}</p>
        {showEmptyAddLink && (
          <Link to="/add" className="btn-primary mt-2 inline-flex text-sm">
            <Plus className="mr-1 h-4 w-4" />
            添加第一株植物
          </Link>
        )}
      </div>
    );
  }
  return (
    <ul className="mt-2 space-y-2">
      {plants.map((p) => (
        <li
          key={p.id}
          className={`flex items-center gap-3 rounded-md border bg-white p-3 ${
            p.id === highlightId
              ? 'border-teamB-primary ring-2 ring-teamB-primary/30'
              : 'border-slate-200'
          }`}
        >
          <SafeImage
            team="b"
            imageUrl={p.firstPhotoUrl}
            imageKey={p.firstPhotoKey}
            alt={p.name}
          />
          <div className="flex-1">
            <p className="text-sm font-semibold">
              {p.name}
              {p.id === highlightId && (
                <span className="ml-1.5 badge bg-emerald-100 text-[10px] text-emerald-700">
                  刚添加
                </span>
              )}
            </p>
            <p className="text-[11px] text-slate-500">
              {p.species} · {p.location}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <Link
              to={`/daily?plantId=${p.id}`}
              className="badge bg-teamB-primary/10 text-teamB-primary text-center"
            >
              去记录
            </Link>
            <Link
              to={`/timeline?plantId=${p.id}`}
              className="badge bg-slate-100 text-slate-600 text-center"
            >
              查看日记
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

interface PlantListSectionProps extends PlantListProps {
  title?: string;
  showAddButton?: boolean;
}

export function PlantListSection({
  title,
  showAddButton,
  ...listProps
}: PlantListSectionProps) {
  const count = listProps.plants.length;
  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">
          {title ?? `我的植物(${count})`}
        </h2>
        {showAddButton && (
          <Link
            to="/add"
            className="flex items-center gap-1 rounded-md bg-teamB-primary px-2.5 py-1 text-xs font-medium text-white hover:bg-teamB-dark"
          >
            <Plus className="h-3.5 w-3.5" />
            添加
          </Link>
        )}
      </div>
      <PlantList {...listProps} />
    </section>
  );
}
